import { useEffect, useMemo, useState } from "react";
import useSubscriptionStore from "../../store/subscription.store";
import PlanCard from "../../components/subscriptions/PlanCard";
import CancelControl from "../../components/subscriptions/CancelControl";
import { Link } from "react-router-dom";
import { ChevronLeft, Check, X, ArrowUp, Sparkles, AlertTriangle, CheckCircle2, FileText } from "lucide-react";
import { checkoutSubscription, getCheckoutStatus } from "../../api/payments.api";
import { formatMoney, planDisplayPrice } from "../../components/billing/formatGstPrice";

// Currencies the SPA surfaces in the picker. Order = display order.
// INR first (default for the existing customer base); foreign currencies
// follow the Sprint 4 launch set (USD/GBP/AUD/EUR).
const CURRENCY_OPTIONS = ["INR", "USD", "GBP", "AUD", "EUR"];

// Plans rendered in this order; everything else (legacy / unknown) falls
// to the end. TRIAL slots after the highest paid B2C tier as a "still
// available if you haven't used it yet" card.
const PLAN_DISPLAY_ORDER = [
  "TRIAL",
  "B2C_BASIC",
  "B2C_INTERMEDIATE",
  // T208: ELITE sits between Intermediate and Advanced in the price
  // ladder (₹6,999 < ₹8,999) but is the "pro freelancer" path — the
  // differentiator is perks (marketplace listing + verified badge +
  // priority support), not raw quotas.
  "B2C_ELITE",
  "B2C_ADVANCED",
  "B2B_CONTACT_SALES",
  // 2026-06-11 — Storage add-ons in ascending GB size. Without this
  // explicit order they fell to the end of sortedPlans tied at the
  // sentinel idx, and the array.sort tie-break inherited the API's
  // alphabetical-by-code order: 100GB, 10GB, 20GB, 2GB, 50GB, 5GB.
  // Founder ask: cheapest → biggest, left to right.
  "STORAGE_2GB",
  "STORAGE_5GB",
  "STORAGE_10GB",
  "STORAGE_20GB",
  "STORAGE_50GB",
  "STORAGE_100GB",
];

// T269 — specialty plans for the player marketplace. Rendered in
// their own section below the standard tier grid because they don't
// fit the "more credits / more storage" comparison axes the standard
// plans share. Player Pro is for cricketers (B2C, ₹199/mo); Scout is
// for talent recruiters (₹4,999/mo).
const SPECIALTY_PLAN_CODES = ["B2C_PLAYER_PRO", "B2B_SCOUT"];

// Hardcoded copy per specialty plan so the cards can lead with the
// value prop rather than the standard "X credits / Y GB" lines that
// don't apply to these tiers.
const SPECIALTY_PLAN_META = {
  B2C_PLAYER_PRO: {
    eyebrow: "For cricketers",
    headline: "Player Pro",
    sub: "₹199 / month",
    bullets: [
      "Verified-by-ZCD badge on your public profile",
      "Featured placement on Find-a-Player",
      "Receive direct DMs from scouts + coaches",
      "Career-stats PDF export",
      "100 ZenCredits / month + 5 GB clip storage",
    ],
  },
  B2B_SCOUT: {
    eyebrow: "For talent recruiters",
    headline: "Scout",
    sub: "₹4,999 / month",
    bullets: [
      "Unlimited search of every published player profile",
      "Advanced filters — region, format, role, batting hand",
      "Player contact email + phone unlocked",
      "Save shortlists · CSV export · lifetime stats depth",
      "Built for agents, T20-franchise scouts, academy heads",
    ],
  },
};

// Feature comparison rows. Each entry is one row in the comparison table.
// `value(plan)` returns whatever the plan exposes for that row (boolean,
// number, or string). Some rows derive from plan.features (the included
// strings array); some from plan.skip_credit_gate; some hardcoded for
// readability (the API doesn't surface "score_match" booleans yet).
const FEATURE_ROWS = [
  // 2026-06-17 T4 — TRIAL.monthly_credits=0 on the Plan but trial users
  // do receive a one-time TRIAL_GRANT of 100 credits at signup. Display
  // the one-time grant explicitly for the TRIAL row so the comparison
  // table doesn't promise a monthly refresh that never fires.
  {
    label: "Monthly credits",
    value: (p) => p.monthly_credits ?? 0,
    fmt: (v, p) => p && p.code === "TRIAL"
      ? "100 (one-time)"
      : (v ? `${v}` : "—"),
  },
  { label: "Storage included",      value: (p) => p.storage_gb_cap ?? 0, fmt: (v) => v ? `${v} GB` : "Custom" },
  { label: "Devices",               value: (p) => p.code === "TRIAL" ? 1 : (p.code === "B2C_ADVANCED" ? "Unlimited" : 1), fmt: (v) => v },
  { label: "Ball-by-ball scoring",  value: () => true },
  // T218: was `(p) => p.code !== "TRIAL" || true` — the `|| true` made
  // the TRIAL exclusion dead code (predicate always returned true).
  // TRIAL plan is bundled with every premium feature (see rows below
  // — voice, PDF, AI ingest all include TRIAL), so the correct
  // intent is "edit is available on all plans". Simplifying to
  // () => true makes that explicit.
  { label: "Edit balls + rebuild",  value: () => true },
  { label: "Reports + CSV export",  value: () => true },
  { label: "Offline mode",          value: () => true },
  { label: "Voice scoring + Sonnet chat", value: (p) => ["B2C_INTERMEDIATE", "B2C_ADVANCED", "B2B_CONTACT_SALES", "TRIAL"].includes(p.code) },
  { label: "Match report PDF",      value: (p) => ["B2C_ADVANCED", "B2B_CONTACT_SALES", "TRIAL"].includes(p.code) },
  { label: "AI ingest (PDF/Excel)", value: (p) => ["B2C_ADVANCED", "B2B_CONTACT_SALES", "TRIAL"].includes(p.code) },
  { label: "AI auto-tag matches",   value: (p) => ["TRIAL", "B2B_CONTACT_SALES"].includes(p.code), help: "Contact sales for paid B2C tiers" },
  { label: "AI Suggest (vision)",   value: (p) => ["TRIAL", "B2B_CONTACT_SALES"].includes(p.code), help: "Contact sales for paid B2C tiers" },
  { label: "Multi-user RBAC",       value: (p) => p.code === "B2B_CONTACT_SALES" },
  { label: "API access",            value: (p) => p.code === "B2B_CONTACT_SALES" },
  // T208: Elite-tier perks. Marketplace + badge + priority support
  // are the differentiators that justify the ₹6,999 price even though
  // raw credits/storage are between Intermediate and Advanced.
  { label: "Scorer-for-hire listing", value: (p) => ["B2C_ELITE", "B2B_CONTACT_SALES"].includes(p.code) },
  { label: "Verified-by-ZCD badge",   value: (p) => ["B2C_ELITE", "B2B_CONTACT_SALES"].includes(p.code) },
  { label: "Priority email + WhatsApp support", value: (p) => ["B2C_ELITE", "B2B_CONTACT_SALES"].includes(p.code) },
  { label: "Early access to new features",      value: (p) => ["B2C_ELITE", "B2B_CONTACT_SALES"].includes(p.code) },
];

function formatCell(row, plan) {
  const v = row.value(plan);
  if (typeof v === "boolean") {
    return v ? (
      <Check className="w-4 h-4 text-emerald-400 mx-auto" />
    ) : (
      <X className="w-4 h-4 text-text-secondary/40 mx-auto" />
    );
  }
  return <span className="text-sm text-text-primary tabular-nums">{row.fmt ? row.fmt(v, plan) : v}</span>;
}

// T269 — Specialty plan CTA. Mirrors PlanCard.handleSubscribe minus
// the full-card chrome (which lives directly above this button in
// the specialty section's custom layout).
function SpecialtySubscribeButton({ plan, isMine }) {
  const [busy, setBusy] = useState(false);

  if (isMine) {
    return (
      <button
        disabled
        className="w-full px-4 py-2.5 rounded-lg bg-emerald-500/20 text-emerald-300 text-sm font-semibold border border-emerald-500/40"
      >
        ✓ Active
      </button>
    );
  }

  const handle = async () => {
    if (busy) return;
    setBusy(true);
    const cleanReturnUrl = window.location.origin + window.location.pathname;
    let res;
    try {
      res = await checkoutSubscription(plan.code, cleanReturnUrl);
    } catch (e) {
      setBusy(false);
      const errCode = e?.response?.data?.code;
      const detail = e?.response?.data?.detail;
      if (errCode === "provider_unavailable") {
        alert(
          "International payments are being activated on our side. " +
          "Please try again in a few days, or email support@zencricketdata.com for assistance."
        );
      } else {
        alert(
          detail
            ? `Could not start subscription: ${detail}`
            : "Something went wrong starting checkout. Try again.",
        );
      }
      return;
    }
    const { provider, authorisation_url, subscription_id, order_id } = res.data || {};
    if (!authorisation_url) {
      setBusy(false);
      const providerLabel = provider === "airwallex" ? "Airwallex" : "Cashfree";
      alert(
        `${providerLabel} didn't return an authorisation URL. ` +
        `The Plan may be missing a provider id — ask admin.`,
      );
      return;
    }
    try {
      sessionStorage.setItem("zcd_pending_subscription", JSON.stringify({
        plan_code: plan.code,
        plan_name: plan.name,
        subscription_id: subscription_id || null,
        order_id: order_id || null,
        ts: Date.now(),
      }));
    } catch { /* private-mode — fine */ }
    window.location.assign(authorisation_url);
  };

  const { currency, majorAmount } = planDisplayPrice(plan);
  const priceLabel = majorAmount != null ? formatMoney(currency, majorAmount) : "—";
  return (
    <button
      onClick={handle}
      disabled={busy}
      className="w-full px-4 py-2.5 rounded-lg bg-button-primary hover:brightness-110 text-white text-sm font-semibold transition disabled:opacity-50"
    >
      {busy ? "Redirecting…" : `Subscribe — ${priceLabel}/mo`}
    </button>
  );
}


export default function Subscriptions() {
  const { plans, fetchPlans, loading, mySubscription } = useSubscriptionStore();
  // Annual / monthly billing toggle — currently informational since the
  // backend only surfaces monthly Plan rows. When the operator picks
  // annual we show the ~17% saving callout + ask them to use the
  // Contact-Sales link to switch. Once the backend exposes yearly Plan
  // variants, this state can be passed down to PlanCard to swap prices.
  const [cycle, setCycle] = useState("monthly");
  // 2026-06-29 — currency picker (Sprint 4). Default INR for backwards
  // compat with the existing customer base; foreign-currency plans
  // (USD/GBP/AUD/EUR) route through Airwallex when selected.
  const [currency, setCurrency] = useState("INR");

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // T217 / P0-5: post-Cashfree return verification. If we find a
  // pending-subscription stash from PlanCard's handleSubscribe AND the
  // user just landed back on this page, check the order status via
  // /api/payments/checkout/status/<order_id>/. We poll the user's
  // subscription too (the webhook flips PENDING → ACTIVE; auth-url
  // return may precede webhook by a few seconds).
  const [subReturnBanner, setSubReturnBanner] = useState(null);
  useEffect(() => {
    let pending = null;
    try {
      const raw = sessionStorage.getItem("zcd_pending_subscription");
      if (raw) pending = JSON.parse(raw);
    } catch { /* corrupt */ }
    if (!pending) return undefined;
    // Stale stash (older than 30 min) — user closed the Cashfree tab
    // ages ago, ignore it so we don't replay verification on every
    // page mount thereafter.
    if (Date.now() - (pending.ts || 0) > 30 * 60 * 1000) {
      try { sessionStorage.removeItem("zcd_pending_subscription"); } catch {}
      return undefined;
    }
    try { sessionStorage.removeItem("zcd_pending_subscription"); } catch {}

    setSubReturnBanner({
      kind: "verifying",
      text: `Verifying your ${pending.plan_name || pending.plan_code} subscription with Cashfree…`,
    });

    let cancelled = false;
    const tick = async () => {
      // First check the order/subscription status if we have an ID.
      if (pending.order_id) {
        try {
          const res = await getCheckoutStatus(pending.order_id);
          if (cancelled) return;
          const status = (res?.data?.status || "").toUpperCase();
          if (status === "PAID" || status === "ACTIVE" || status === "SUCCESS") {
            setSubReturnBanner({
              kind: "success",
              text: `✓ ${pending.plan_name || pending.plan_code} activated. Welcome aboard.`,
            });
            // Refresh the canonical subscription state.
            useSubscriptionStore.getState().fetchMySubscription?.();
            return;
          }
          if (["EXPIRED", "CANCELLED", "TERMINATED", "FAILED"].includes(status)) {
            setSubReturnBanner({
              kind: "fail",
              text: `Subscription was ${status.toLowerCase()}. No money was charged.`,
            });
            return;
          }
        } catch { /* fall through to retry */ }
      }
      // Status unknown / pending — poll again after 3s, up to 150s.
      if (!cancelled) setTimeout(tick, 3000);
    };
    // 2026-06-17 F3 — was 30s; bumped to 150s so the reconcile-cron
    // path (which can take up to 2 min when the webhook is broken) has
    // time to land before we tell the user "still confirming". Surfaces
    // progressive copy mid-way so the user knows we're still working.
    const milestone1 = setTimeout(() => {
      setSubReturnBanner((cur) => cur?.kind === "verifying" ? {
        kind: "verifying",
        text: "Still verifying with the bank — most subscriptions activate within 60 seconds.",
      } : cur);
    }, 30000);
    const milestone2 = setTimeout(() => {
      setSubReturnBanner((cur) => cur?.kind === "verifying" ? {
        kind: "verifying",
        text: "Taking a bit longer than usual — running our reconciliation check (up to 2 min).",
      } : cur);
    }, 90000);
    const stopTimer = setTimeout(() => {
      cancelled = true;
      setSubReturnBanner((cur) => cur?.kind === "verifying" ? {
        kind: "pending",
        text: "Mandate still confirming. Refresh in a minute; if the plan card doesn't show Active, contact admin with the order ID.",
      } : cur);
    }, 150000);
    tick();
    return () => {
      cancelled = true;
      clearTimeout(stopTimer);
      clearTimeout(milestone1);
      clearTimeout(milestone2);
    };
  }, []);

  // Sort plans into the canonical display order so cards are predictable
  // (trial → basic → intermediate → advanced → org), and the comparison
  // table reads left-to-right cheapest → most-expensive.
  // T269 — sortedPlans drops specialty plans (Player Pro / Scout); they
  // render in their own section below. Keeps the standard 5-tier grid +
  // comparison table tidy.
  const sortedPlans = useMemo(() => {
    if (!plans?.length) return [];
    const idx = (code) => {
      const i = PLAN_DISPLAY_ORDER.indexOf(code);
      return i === -1 ? PLAN_DISPLAY_ORDER.length : i;
    };
    return [...plans]
      .filter((p) => !SPECIALTY_PLAN_CODES.includes(p.code))
      // 2026-06-29 — currency-pick filter. INR plans default-fill the
      // legacy code path (Plan.currency NULL on legacy rows treated as
      // INR). Foreign-currency plans only show when their currency is
      // explicitly selected.
      .filter((p) => (p.currency || "INR").toUpperCase() === currency)
      .sort((a, b) => idx(a.code) - idx(b.code));
  }, [plans, currency]);

  // Which currencies actually have at least one active plan — drives the
  // picker so we don't surface USD/GBP/AUD/EUR pills before plans are
  // seeded in the admin.
  const availableCurrencies = useMemo(() => {
    const set = new Set();
    (plans || []).forEach((p) => set.add((p.currency || "INR").toUpperCase()));
    return CURRENCY_OPTIONS.filter((c) => set.has(c));
  }, [plans]);

  // 2026-06-10 founder ask — split sortedPlans into two tabs by
  // Plan.kind:
  //   - codingPlans (kind=MAIN): TRIAL + B2C_BASIC/INTERMEDIATE/ELITE
  //     /ADVANCED + B2B_CONTACT_SALES. The "coding app" subscriptions.
  //   - storagePlans (kind=STORAGE_ADDON): the recurring storage add-on
  //     plans (2GB, 5GB, 10GB...) that stack on top of the MAIN
  //     plan's storage_gb_cap.
  // Fallback: if `kind` is missing on the API row (old backend), treat
  // it as MAIN so nothing disappears for users on legacy serialisers.
  const codingPlans = useMemo(
    () => sortedPlans.filter((p) => (p.kind || "MAIN") === "MAIN"),
    [sortedPlans],
  );
  const storagePlans = useMemo(
    () => sortedPlans.filter((p) => p.kind === "STORAGE_ADDON"),
    [sortedPlans],
  );
  const [planTab, setPlanTab] = useState("coding"); // "coding" | "storage"

  const specialtyPlans = useMemo(() => {
    if (!plans?.length) return [];
    return SPECIALTY_PLAN_CODES
      .map((code) => plans.find((p) => p.code === code))
      .filter(Boolean);
  }, [plans]);

  // Compute the next-tier upsell suggestion: how much more would the
  // current plan customer pay to move up one rung, and what do they get?
  const upsell = useMemo(() => {
    if (!mySubscription?.plan) return null;
    const order = ["B2C_BASIC", "B2C_INTERMEDIATE", "B2C_ADVANCED"];
    const myIdx = order.indexOf(mySubscription.plan.code);
    if (myIdx === -1 || myIdx === order.length - 1) return null;
    const nextCode = order[myIdx + 1];
    const next = sortedPlans.find((p) => p.code === nextCode);
    if (!next || next.price_inr == null) return null;
    const cur = sortedPlans.find((p) => p.code === mySubscription.plan.code);
    if (!cur || cur.price_inr == null) return null;
    return {
      next,
      diff: next.price_inr - cur.price_inr,
      extraCredits: (next.monthly_credits || 0) - (cur.monthly_credits || 0),
      extraGb: (next.storage_gb_cap || 0) - (cur.storage_gb_cap || 0),
    };
  }, [mySubscription, sortedPlans]);

  if (loading)
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <div className="text-text-secondary">Loading plans...</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-bg-main py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center gap-2 flex-wrap">
          <Link
            to="/dashboard"
            className="flex gap-2 text-text-primary bg-button-secondary hover:bg-button-primary transition-all duration-150 ease-in-out px-4 py-2 rounded"
          >
            <ChevronLeft className="w-5" />
            Go Back to Dashboard
          </Link>
          <Link
            to="/account/invoices"
            className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary px-3 py-2 rounded border border-bg-secondary hover:border-button-primary"
          >
            <FileText className="w-4 h-4" />
            Tax invoices
          </Link>
        </div>

        {/* T217 / P0-5: post-Cashfree return banner. Surfaces the
            verification state so the operator knows exactly what
            happened to their mandate after the redirect. */}
        {subReturnBanner && (
          <div className={`mt-4 px-4 py-3 rounded-lg flex items-center gap-3 text-sm ${
            subReturnBanner.kind === "success"
              ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200"
              : subReturnBanner.kind === "fail"
                ? "bg-red-500/10 border border-red-500/40 text-red-200"
                : "bg-amber-500/10 border border-amber-500/40 text-amber-200"
          }`}>
            {subReturnBanner.kind === "success"
              ? <CheckCircle2 className="w-4 h-4 shrink-0" />
              : subReturnBanner.kind === "fail"
                ? <AlertTriangle className="w-4 h-4 shrink-0" />
                : <div className="w-3 h-3 rounded-full border-2 border-amber-300 border-t-transparent animate-spin shrink-0" />}
            <span>{subReturnBanner.text}</span>
            <button
              onClick={() => setSubReturnBanner(null)}
              className="ml-auto text-xs opacity-70 hover:opacity-100"
              aria-label="Dismiss"
            >×</button>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12 mt-6">
          <h1 className="text-4xl font-bold text-text-primary mb-4">
            Choose Your Plan
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Pricing scales with usage. Upgrade or downgrade any time.
          </p>

          {mySubscription && (
            <>
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-bg-primary rounded-full border border-bg-secondary">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-text-secondary">
                  Current plan:{" "}
                  <span className="text-text-primary font-medium">
                    {mySubscription.plan?.name || "—"}
                  </span>
                </span>
              </div>
              {/* T250 — "Ends on …" pill only. The cancel-trigger itself
                  moved to a dedicated section below the plans grid so
                  it's easier to find (2026-07-06 founder ask). This
                  inline call still renders the scheduled-end pill +
                  reactivate button when the sub is already scheduled to
                  end, so returning users see it without scrolling. */}
              {mySubscription.cancel_at_period_end && (
                <CancelControl
                  subscription={mySubscription}
                  onUpdated={() => {
                    useSubscriptionStore.getState().fetchMySubscription();
                    fetchPlans();
                  }}
                />
              )}
            </>
          )}

          {/* T247.J — Elite perks call-to-action. Only renders when the
              user is currently on B2C_ELITE so they can act on the
              marketplace-listing perk they're paying for. */}
          {mySubscription?.plan?.code === "B2C_ELITE" && (
            <div className="mt-6 max-w-2xl mx-auto rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-text-primary">Elite perks active</div>
                  <p className="text-sm text-text-secondary">
                    Your Elite plan includes a public scorer-for-hire profile + the Verified-by-ZCD badge.
                    Build your profile so tournament organisers can find you.
                  </p>
                  <Link
                    to="/account/portfolio"
                    className="inline-flex items-center gap-1 mt-2 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-text-primary rounded text-sm font-medium border border-amber-500/40"
                  >
                    Manage public profile →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Billing-cycle toggle. The plans listed below are all priced
              monthly; choosing "Annual" surfaces the marketing savings
              + a call-to-action to switch via sales (backend doesn't
              yet expose yearly Plan rows). */}
          <div className="mt-6 inline-flex items-center bg-bg-primary rounded-full border border-bg-secondary p-1">
            {["monthly", "annual"].map((c) => (
              <button
                key={c}
                onClick={() => setCycle(c)}
                className={`text-xs uppercase tracking-wider px-4 py-1.5 rounded-full ${
                  cycle === c
                    ? "bg-button-primary text-white font-semibold"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                {c === "monthly" ? "Monthly" : "Annual · save ~17%"}
              </button>
            ))}
          </div>

          {/* 2026-06-29 — currency picker. Only renders when more than
              one currency is seeded (single-currency tenants stay on the
              legacy INR-only chrome). Foreign-currency plans route
              through Airwallex on subscribe; INR stays on Cashfree. */}
          {availableCurrencies.length > 1 && (
            <div className="mt-4 inline-flex items-center bg-bg-primary rounded-full border border-bg-secondary p-1">
              {availableCurrencies.map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full ${
                    currency === c
                      ? "bg-button-primary text-white font-semibold"
                      : "text-text-secondary hover:text-text-primary"
                  }`}
                  aria-pressed={currency === c}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          {currency !== "INR" && (
            <p className="mt-2 text-[11px] text-text-secondary">
              International billing via Airwallex · cards, Apple Pay, Google Pay
            </p>
          )}
          {cycle === "annual" && (
            <div className="mt-4 max-w-xl mx-auto rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-sm px-4 py-3 flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="text-left">
                Annual prices below are <strong>10 months for the price of 12</strong> (~17% off vs paying month-to-month). Click <strong>Subscribe Now</strong> on the plan you want — our admin will set up the annual cycle for you.
              </div>
            </div>
          )}
        </div>

        {/* Upsell strip — concrete delta from current plan to next tier */}
        {upsell && (
          <div className="max-w-3xl mx-auto mb-10 p-4 bg-gradient-to-br from-purple-500/15 to-bg-primary border border-purple-500/40 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <ArrowUp className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-text-primary font-medium">
                  Upgrade to <strong>{upsell.next.name}</strong> for ₹{upsell.diff} more
                </div>
                <div className="text-text-secondary text-sm mt-0.5">
                  + {upsell.extraCredits} more credits/month
                  {upsell.extraGb > 0 ? ` · + ${upsell.extraGb} GB storage` : ""}
                </div>
              </div>
            </div>
            <a
              href={`#plan-${upsell.next.code}`}
              className="shrink-0 px-4 py-2 rounded-md bg-purple-500 text-white text-sm font-medium hover:bg-purple-400"
            >
              See {upsell.next.name} →
            </a>
          </div>
        )}

        {/* 2026-06-10 founder ask — tab switcher between Coding app
            plans (kind=MAIN) and Storage plans (kind=STORAGE_ADDON). */}
        <div className="flex items-center gap-2 mb-4 border-b border-bg-secondary">
          <button
            type="button"
            onClick={() => setPlanTab("coding")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              planTab === "coding"
                ? "border-button-primary text-text-primary"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            Coding app plans
            <span className="ml-1.5 text-[10px] opacity-60 font-mono">
              {codingPlans.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setPlanTab("storage")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              planTab === "storage"
                ? "border-button-primary text-text-primary"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            Storage plans
            <span className="ml-1.5 text-[10px] opacity-60 font-mono">
              {storagePlans.length}
            </span>
          </button>
        </div>

        {/* Plans grid — T219: explicit grid-cols-1 base + sm: breakpoint
            so the cards stack cleanly on mobile and progress through
            2 → 3 → 5 columns as the viewport grows. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-12">
          {(planTab === "coding" ? codingPlans : storagePlans).map((plan) => (
            <div key={plan.code} id={`plan-${plan.code}`}>
              <PlanCard plan={plan} currentPlan={mySubscription?.plan} cycle={cycle} />
            </div>
          ))}
          {planTab === "storage" && storagePlans.length === 0 && (
            <div className="col-span-full text-center text-text-secondary text-sm py-8">
              {currency === "INR"
                ? "No storage add-on plans available yet. They'll appear here once your admin enables them."
                : `No ${currency} storage plans seeded yet. Switch to INR or contact us for international storage pricing.`}
            </div>
          )}
          {planTab === "coding" && codingPlans.length === 0 && currency !== "INR" && (
            <div className="col-span-full text-center text-text-secondary text-sm py-8">
              No {currency} plans seeded yet. Switch the currency above, or contact us for tailored international pricing.
            </div>
          )}
        </div>

        {/* Manage subscription — placed directly below the plans grid so
            it's discoverable for anyone with a live payment mandate.
            Uses the backend-computed `can_cancel` flag so mandate-trials
            (is_trial=true but a Cashfree/Airwallex mandate exists) also
            see the cancel path — the whole point of the mandate-trial
            UX is that you can bail out before the first debit fires.
            Legacy free trials (no mandate) stay hidden. Founder ask
            2026-07-06. */}
        {mySubscription?.can_cancel && (
          <div className="mb-12 max-w-2xl mx-auto rounded-2xl border border-bg-secondary bg-bg-primary p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-text-primary">
                  Manage your subscription
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  Cancelling revokes your payment mandate and stops autopay
                  immediately — no further charges will be attempted. Your
                  service stays active until the end of your current
                  billing period, then moves to the free tier.
                </p>
              </div>
              <div className="shrink-0">
                <CancelControl
                  subscription={mySubscription}
                  variant="section"
                  onUpdated={() => {
                    useSubscriptionStore.getState().fetchMySubscription();
                    fetchPlans();
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* T269 — Specialty plans (Player Pro + Scout). Different
            framing: one card per persona, value-prop-first copy. The
            standard PlanCard handles checkout exactly the same way.
            2026-06-10 — gated to the Coding tab; these are coding-side
            paid plans, not storage. */}
        {planTab === "coding" && specialtyPlans.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-text-primary">
                Specialty plans
              </h2>
              <p className="text-sm text-text-secondary mt-1">
                Built around the player marketplace — for cricketers who
                want to be findable and recruiters who do the finding.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {specialtyPlans.map((plan) => {
                const meta = SPECIALTY_PLAN_META[plan.code] || {};
                const isMine = mySubscription?.plan?.code === plan.code;
                return (
                  <div
                    key={plan.code}
                    id={`plan-${plan.code}`}
                    className={`relative bg-bg-primary border rounded-2xl p-5 ${
                      isMine
                        ? "border-emerald-500/60"
                        : "border-bg-secondary"
                    }`}
                  >
                    {isMine && (
                      <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                        Current plan
                      </span>
                    )}
                    {meta.eyebrow && (
                      <p className="text-[11px] uppercase tracking-wider text-button-primary font-semibold mb-1">
                        {meta.eyebrow}
                      </p>
                    )}
                    <h3 className="text-xl font-bold text-text-primary">
                      {meta.headline || plan.name}
                    </h3>
                    <p className="text-sm text-text-secondary mb-3">
                      {meta.sub || (plan.price_inr ? `₹${plan.price_inr.toLocaleString("en-IN")} / month` : "")}
                    </p>
                    <ul className="space-y-1.5 mb-4 text-sm">
                      {(meta.bullets || []).map((b) => (
                        <li key={b} className="flex items-start gap-2 text-text-secondary">
                          <Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <SpecialtySubscribeButton plan={plan} isMine={isMine} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Real feature comparison table. T219: on mobile the 6-column
            table is unusable (horizontal scroll with no indicator),
            so we render a stacked card layout below sm:hidden. The
            md:block version preserves the side-by-side table on
            tablet+ where it fits naturally.
            2026-06-10 — gated to the Coding tab; FEATURE_ROWS are
            all coding-app axes (credits, voice, AI), don't apply to
            storage add-ons. */}
        {planTab === "coding" && (
        <div className="bg-bg-primary rounded-2xl p-4 sm:p-8 border border-bg-secondary mb-12">
          <h2 className="text-2xl font-semibold text-text-primary mb-2 text-center">
            Feature comparison
          </h2>
          <p className="text-sm text-text-secondary text-center mb-6">
            Side-by-side breakdown of every plan.
          </p>
          {/* Mobile card layout (< 768px) — one collapsible card per plan. */}
          <div className="md:hidden space-y-3">
            {sortedPlans.map((p) => (
              <details
                key={p.code}
                className={`bg-bg-secondary/30 rounded-lg border ${
                  p.code === mySubscription?.plan?.code
                    ? "border-emerald-500/40"
                    : "border-bg-secondary"
                }`}
                open={p.code === mySubscription?.plan?.code}
              >
                <summary className="cursor-pointer px-3 py-2 flex justify-between items-center">
                  <span className={`font-medium text-sm ${p.code === mySubscription?.plan?.code ? "text-emerald-400" : "text-text-primary"}`}>
                    {p.name}
                    {p.code === mySubscription?.plan?.code && (
                      <span className="ml-2 text-[10px] text-emerald-400/80 uppercase">current</span>
                    )}
                  </span>
                  <span className="text-xs text-text-secondary">tap to expand</span>
                </summary>
                <ul className="px-3 pb-3 pt-1 divide-y divide-bg-secondary/40">
                  {FEATURE_ROWS.map((row) => (
                    <li key={row.label} className="flex items-center justify-between py-1.5 text-xs">
                      <span className="text-text-secondary">
                        {row.label}
                        {row.help && <span className="ml-1 opacity-60" title={row.help}>ⓘ</span>}
                      </span>
                      <span>{formatCell(row, p)}</span>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
          {/* Desktop/tablet table layout (md+). overflow-x-auto wrapper
              kicks in if the table is wider than the viewport — 5+ plans
              at md=768 can get tight. */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-bg-secondary">
                <th className="text-left text-xs uppercase tracking-wider text-text-secondary font-medium py-3 pr-4">
                  Feature
                </th>
                {sortedPlans.map((p) => (
                  <th
                    key={p.code}
                    className={`text-center text-xs uppercase tracking-wider font-medium py-3 px-2 ${
                      p.code === mySubscription?.plan?.code
                        ? "text-emerald-400"
                        : "text-text-primary"
                    }`}
                  >
                    {p.name}
                    {p.code === mySubscription?.plan?.code && (
                      <div className="text-[10px] text-emerald-400/80 mt-0.5 normal-case">current</div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_ROWS.map((row, i) => (
                <tr
                  key={row.label}
                  className={i % 2 === 0 ? "bg-bg-secondary/20" : ""}
                >
                  <td className="py-2 pr-4 text-sm text-text-primary">
                    {row.label}
                    {row.help && (
                      <span
                        className="ml-1 text-text-secondary cursor-help"
                        title={row.help}
                      >
                        ⓘ
                      </span>
                    )}
                  </td>
                  {sortedPlans.map((p) => (
                    <td key={p.code} className="py-2 px-2 text-center">
                      {formatCell(row, p)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        )}

        {/* Team-code redemption was moved to the Login page 2026-05-25
            (founder ask). Users redeem at sign-in; no panel needed here. */}

        {/* FAQ pointer */}
        <div className="text-center">
          <p className="text-text-secondary">
            Have questions?{" "}
            <a
              href="mailto:a.manivannan@sportingzenminds.com?subject=ZenCricketData plan question"
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              Email support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
